/**
 * Phone number validation utilities
 */

// Phone number patterns for different formats
const PHONE_PATTERNS = [
  /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/, // US format
  /^\+?([0-9]{1,3})[-.\s]?([0-9]{3,4})[-.\s]?([0-9]{3,4})[-.\s]?([0-9]{3,4})$/, // International
  /^[0-9]{10}$/, // 10 digits
  /^[0-9]{11}$/, // 11 digits with country code
];

/**
 * Checks if a string appears to be a phone number
 * @param input - The input string to check
 * @returns boolean - True if the input appears to be a phone number
 */
export const isPhoneNumber = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  // Remove common non-digit characters for pattern matching
  const cleanInput = input.replace(/[\s\-\.\(\)\+]/g, '');
  
  // Check if it's mostly digits (allowing for some formatting)
  const digitCount = (input.match(/\d/g) || []).length;
  const totalLength = input.length;
  
  // Must have at least 10 digits and digits should be majority of characters
  if (digitCount < 10 || digitCount / totalLength < 0.7) {
    return false;
  }

  // Check against known phone patterns
  return PHONE_PATTERNS.some(pattern => pattern.test(input));
};

/**
 * Checks if a question is asking for a phone number based on context
 * @param questionText - The question text to analyze
 * @param questionType - The type of question (text, number, etc.)
 * @returns boolean - True if the question appears to be asking for a phone number
 */
export const isPhoneQuestion = (questionText: string, questionType: string): boolean => {
  if (!questionText || typeof questionText !== 'string') {
    return false;
  }

  // Keywords that suggest a phone number question
  const phoneKeywords = [
    'phone',
    'telephone',
    'cell',
    'mobile',
    'contact number',
    'phone number',
    'telephone number',
    'cell phone',
    'mobile number',
    'contact info',
    'reach you',
    'call you',
    'callback',
    'call back'
  ];

  const lowerText = questionText.toLowerCase();
  
  // Check if question contains phone-related keywords
  const containsPhoneKeywords = phoneKeywords.some(keyword => 
    lowerText.includes(keyword)
  );

  // Additional check: if it's a number type question with context suggesting phone
  const isNumberTypeWithPhoneContext = questionType === 'number' && (
    lowerText.includes('number') || 
    lowerText.includes('contact') ||
    lowerText.includes('reach')
  );

  return containsPhoneKeywords || isNumberTypeWithPhoneContext;
};

/**
 * Validates a phone number format
 * @param phone - The phone number to validate
 * @returns object - Validation result with isValid and message
 */
export const validatePhoneNumber = (phone: string): { isValid: boolean; message?: string } => {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, message: 'Phone number is required' };
  }

  const trimmedPhone = phone.trim();
  
  if (trimmedPhone.length < 10) {
    return { isValid: false, message: 'Phone number must be at least 10 digits' };
  }

  if (!isPhoneNumber(trimmedPhone)) {
    return { isValid: false, message: 'Please enter a valid phone number' };
  }

  return { isValid: true };
};

/**
 * Formats a phone number for display
 * @param phone - The phone number to format
 * @returns string - Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX for US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // Format as +X (XXX) XXX-XXXX for 11-digit numbers
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  // Return original if we can't format
  return phone;
};